// react
import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { useTheme } from '@mui/material/styles';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

// relative
import type { ChartSeries, DateRange, SeriesVisibility, YAxis } from '../types';
import { getSeriesColorByIndex } from '../utils/seriesColors';

import { DateTime } from 'luxon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HighchartsAny = any;

interface UseChartOptionsArgs {
  chartSeries: ChartSeries[];
  liveMode: boolean;
  rawDataMode: boolean;
  yAxes: YAxis[];
  seriesVisibility: SeriesVisibility;
  setSeriesVisibility: Dispatch<SetStateAction<SeriesVisibility>>;
  dateRange?: DateRange;
  selectedDateRange?: DateRange;
  onAfterSetExtremes?: (min: number, max: number) => void;
}

function getChartLocales(language?: string) {
  if (language === 'dk') {
    return {
      luxonLocale: 'da',
      numberLocale: 'da-DK',
    };
  }

  return {
    luxonLocale: language || undefined,
    numberLocale: language || undefined,
  };
}

// Module-level cache for Intl.NumberFormat instances.
// Creating `new Intl.NumberFormat` is expensive (~10–50µs). With a shared tooltip
// over 30 series at 60fps, uncached construction costs ~1,800 allocations/sec.
// The cache has at most 3 entries per locale (0, 1, or 2 decimal places).
const _numberFormatCache = new Map<string, Intl.NumberFormat>();

function getCachedNumberFormat(
  locale: string | undefined,
  maximumFractionDigits: number,
): Intl.NumberFormat {
  const key = `${locale ?? ''}|${maximumFractionDigits}`;
  let fmt = _numberFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, { maximumFractionDigits });
    _numberFormatCache.set(key, fmt);
  }
  return fmt;
}

function formatChartNumber(
  value: number,
  locale?: string,
  maximumFractionDigits?: number,
) {
  if (value === null || value === undefined) return '-';

  const calculatedMaximumFractionDigits =
    maximumFractionDigits ?? (value > 100 ? 0 : value > 10 ? 1 : 2);

  return getCachedNumberFormat(locale, calculatedMaximumFractionDigits).format(
    value,
  );
}

function getSeriesGapSize(data: [number, number][]) {
  if (data.length < 3) return undefined;

  const sampleSize = Math.min(data.length - 1, 25);
  const step = Math.max(1, Math.floor((data.length - 1) / sampleSize));
  const intervals: number[] = [];

  for (let index = step; index < data.length; index += step) {
    const interval = data[index][0] - data[index - step][0];
    if (interval > 0) {
      intervals.push(interval);
    }
  }

  intervals.sort((a, b) => a - b);

  if (intervals.length === 0) return undefined;

  const medianInterval = intervals[Math.floor(intervals.length / 2)];

  return medianInterval > 0 ? medianInterval * 4 : undefined;
}

type DataPoint =
  | [number, number]
  | { x: number; y: number; marker: { enabled: boolean; radius: number } };

// For a series that has gap-breaking enabled, mark any point that is isolated
// (gap on both sides, or at the boundary of the series) so Highcharts renders a
// visible dot even though no line connects to it.
function markIsolatedPoints(
  data: [number, number][],
  gapSize: number,
): DataPoint[] {
  return data.map((point, i) => {
    const prev = data[i - 1];
    const next = data[i + 1];
    const prevGap = !prev || point[0] - prev[0] > gapSize;
    const nextGap = !next || next[0] - point[0] > gapSize;
    if (prevGap && nextGap) {
      return { x: point[0], y: point[1], marker: { enabled: true, radius: 4 } };
    }
    return point;
  });
}

export default function useChartOptions({
  chartSeries,
  liveMode,
  rawDataMode,
  yAxes,
  seriesVisibility,
  setSeriesVisibility,
  dateRange,
  selectedDateRange,
  onAfterSetExtremes,
}: UseChartOptionsArgs) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
  const { luxonLocale, numberLocale } = getChartLocales(i18n.resolvedLanguage);
  const allDataTypes = useMemo(
    () => Array.from(new Set(chartSeries.map((s) => s.dataType))),
    [chartSeries],
  );
  const axisIndexByDataType = useMemo(
    () => new Map(allDataTypes.map((dataType, index) => [dataType, index])),
    [allDataTypes],
  );
  const axisByDataType = useMemo(
    () => new Map(yAxes.map((axis) => [axis.title.text, axis])),
    [yAxes],
  );
  const baseUnitByDataType = useMemo(() => {
    const map = new Map<string, string>();

    chartSeries.forEach((series) => {
      if (!map.has(series.dataType)) {
        map.set(series.dataType, series.base ?? '');
      }
    });

    return map;
  }, [chartSeries]);
  const filteredAxes = useMemo(
    () =>
      allDataTypes.map((dt, idx) => {
        const axis = axisByDataType.get(dt);
        return axis
          ? {
              ...axis,
              title: { text: '' },
              gridLineColor: themeOptions.yAxis.gridLineColor,
              labels: {
                formatter: (point: { value: number }) =>
                  `${formatChartNumber(point.value, numberLocale, 2)} ${baseUnitByDataType.get(dt) ?? ''}`,
              },
            }
          : {
              title: { text: dt },
              opposite: idx % 2 === 1,
              gridLineColor: themeOptions.yAxis.gridLineColor,
            };
      }),
    [
      allDataTypes,
      axisByDataType,
      baseUnitByDataType,
      numberLocale,
      themeOptions,
    ],
  );

  // processedSeries: expensive per-point work (gap detection, isolated-point marking,
  // color pinning) runs only when chart data or axis mapping changes — NOT on every
  // visibility toggle. With 30 series this avoids O(n × dataLength) work on each toggle.
  const processedSeries = useMemo(
    () =>
      chartSeries.map((s, idx) => {
        const gapSize = getSeriesGapSize(s.data);
        const data: DataPoint[] =
          gapSize !== undefined ? markIsolatedPoints(s.data, gapSize) : s.data;
        return {
          ...s,
          data,
          gapSize,
          gapUnit: 'value' as const,
          dataGrouping: { enabled: false },
          // Explicitly pin the color so it never changes when other series are
          // hidden/shown and Highcharts recalculates auto-assigned colorIndex.
          color: getSeriesColorByIndex(idx),
          // Markers are disabled globally; isolated points (gap on both sides)
          // override this via per-point marker config injected by markIsolatedPoints.
          marker: { enabled: false, radius: 3 },
          // Disable per-series boost so per-point marker config is respected.
          // Chart-level seriesThreshold is also set to Infinity to prevent it
          // from overriding this when many series are active.
          ...(gapSize !== undefined && { boostThreshold: 1e9 }),
          yAxis: axisIndexByDataType.get(s.dataType) ?? 0,
        };
      }),
    [axisIndexByDataType, chartSeries],
  );

  // allSeries: applies visibility on top of the pre-processed data — O(n) spread,
  // cheap enough that seriesVisibility changes don't trigger the expensive path above.
  const allSeries = useMemo(
    () =>
      processedSeries.map((s) => ({
        ...s,
        visible: seriesVisibility[s.id]?.visible !== false,
      })),
    [processedSeries, seriesVisibility],
  );
  const fromMs = dateRange?.from?.toMillis();
  const toMs = dateRange?.to?.toMillis();
  const navigatorFromMs = selectedDateRange?.from?.toMillis() ?? fromMs;
  const navigatorToMs = selectedDateRange?.to?.toMillis() ?? toMs;

  return {
    chart: {
      animation: false,
      height: 600,
      zooming: { type: 'x', mouseWheel: { enabled: false } },
      boost: {
        useGPUTranslations: true,
        // Prevent chart-level boost from overriding per-series boostThreshold.
        // Sparse series opt out of boost so their per-point marker config
        // (injected for isolated points) is respected by Highcharts.
        seriesThreshold: Infinity,
      },
      events: {
        load() {
          if (this.__mouseleaveHandler) return;

          const updateZoomMode = (isShiftPressed: boolean) => {
            this.update(
              {
                chart: {
                  zooming: {
                    type: isShiftPressed ? 'y' : 'x',
                    mouseWheel: { enabled: false },
                  },
                },
              },
              false,
            );
            this.redraw();
          };

          this.__keydownHandler = (event: KeyboardEvent) => {
            if (event.key === 'Shift' && !this.__isShiftPressed) {
              this.__isShiftPressed = true;
              updateZoomMode(true);
            }
          };

          this.__keyupHandler = (event: KeyboardEvent) => {
            if (event.key === 'Shift' && this.__isShiftPressed) {
              this.__isShiftPressed = false;
              updateZoomMode(false);
            }
          };

          this.__windowBlurHandler = () => {
            if (this.__isShiftPressed) {
              this.__isShiftPressed = false;
              updateZoomMode(false);
            }
          };

          this.__isShiftPressed = false;
          window.addEventListener('keydown', this.__keydownHandler);
          window.addEventListener('keyup', this.__keyupHandler);
          window.addEventListener('blur', this.__windowBlurHandler);

          this.__mouseleaveHandler = () => {
            // Tooltip
            this.tooltip?.hide(0);

            // Clear hovered points (shared tooltip can use hoverPoints)
            if (this.hoverPoints?.length) {
              this.hoverPoints.forEach((p) => p.setState(''));
              this.hoverPoints.length = 0;
            }

            // Clear single hovered point
            if (this.hoverPoint) {
              this.hoverPoint.setState('');
              this.hoverPoint = null;
            }

            // Clear hovered series (removes “glow/thickening” highlight)
            if (this.hoverSeries) {
              this.hoverSeries.setState('');
              this.hoverSeries = null;
            }

            // Clear series/point states (also removes inactive dimming)
            this.series.forEach((s) => {
              s.setState('');
              // Points can keep state in some cases (markers/halos)
              if (s.points) s.points.forEach((p) => p.setState(''));
            });

            // Reset pointer + crosshairs
            this.pointer?.reset?.(true);
            this.xAxis?.forEach((a) => a.hideCrosshair?.());
            this.yAxis?.forEach((a) => a.hideCrosshair?.());
          };

          this.container.addEventListener(
            'mouseleave',
            this.__mouseleaveHandler,
          );
        },
        destroy() {
          if (this.__keydownHandler) {
            window.removeEventListener('keydown', this.__keydownHandler);
            this.__keydownHandler = null;
          }

          if (this.__keyupHandler) {
            window.removeEventListener('keyup', this.__keyupHandler);
            this.__keyupHandler = null;
          }

          if (this.__windowBlurHandler) {
            window.removeEventListener('blur', this.__windowBlurHandler);
            this.__windowBlurHandler = null;
          }

          this.__isShiftPressed = false;

          if (this.__mouseleaveHandler) {
            this.container.removeEventListener(
              'mouseleave',
              this.__mouseleaveHandler,
            );
            this.__mouseleaveHandler = null;
          }
        },
      },
    },
    title: { text: '' },
    yAxis: filteredAxes,
    series: allSeries,
    time: { useUTC: false },
    xAxis: {
      scrollbar: { enabled: false, liveRedraw: false },
      type: 'datetime',
      min: fromMs,
      max: toMs,
      // Preserve real time gaps so users can zoom arbitrary windows, including empty ranges.
      ordinal: false,
      events: {
        afterSetExtremes(e: HighchartsAny) {
          if (
            (e.trigger === 'zoom' || e.trigger === 'navigator') &&
            onAfterSetExtremes
          ) {
            onAfterSetExtremes(e.min, e.max);
          }
        },
      },
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      formatter() {
        const dt = DateTime.fromMillis(this.x)
          .setLocale(luxonLocale)
          .toLocaleString(
            liveMode || rawDataMode
              ? DateTime.DATETIME_MED_WITH_SECONDS
              : DateTime.DATETIME_MED,
          );
        let s = `<b>${dt}</b><br/>`;
        if (this.points) {
          // Group points by unit
          const byUnit = new Map<string, HighchartsAny[]>();
          this.points.forEach((point: HighchartsAny) => {
            const unit = point.series.options.unit as
              | { UnitId: number; Name: string }
              | undefined;
            const key = unit ? `[${unit.UnitId}] ${unit.Name}` : '';
            const arr = byUnit.get(key) ?? [];
            arr.push(point);
            byUnit.set(key, arr);
          });
          byUnit.forEach((points, unitLabel) => {
            if (unitLabel) {
              s += `<br/><span style="font-weight:600;font-size:0.9em">${unitLabel}</span><br/>`;
            }
            points.forEach((point: HighchartsAny) => {
              s += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatChartNumber(point.y, numberLocale)}</b><br/>`;
            });
          });
        } else {
          s += `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formatChartNumber(this.y, numberLocale)}</b><br/>`;
        }
        return s;
      },
    },
    plotOptions: {
      series: {
        dataGrouping: { enabled: false },
        states: { hover: { enabled: false } },
        boostThreshold: liveMode ? 2000 : 1000,
        // Use x-only snapping for the shared tooltip. For time series this is
        // both correct semantics and faster: O(log n) binary search on x rather
        // than the default 2-D proximity calculation across all 30 series.
        findNearestPointBy: 'x',
        events: {
          legendItemClick(event: HighchartsAny) {
            event.preventDefault();
            const id = this.options.id as string;
            setSeriesVisibility((prev) => ({
              ...prev,
              [id]: { visible: !(prev[id]?.visible !== false) },
            }));
            return false;
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    navigator: {
      enabled: !liveMode,
      xAxis: {
        min: navigatorFromMs,
        max: navigatorToMs,
      },
    },
  };
}
